import { useCallback, useEffect, useRef, useState } from 'react'
import { useGetVisitsVisitIdV2 } from 'api/generated/visit-controller-v2/visit-controller-v2'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { VisitMethods } from 'constants/VisitMethods'
import { type PatientWithVisitInfo } from 'types/types'

type UseInPersonVisitNoticeReturn = {
  isNoticeOpen: boolean
  confirmNotice: () => void
}

/** The notice is about contacting the patient remotely, so only live remote visits get it. */
const NOTICED_VISIT_METHODS: readonly string[] = [VisitMethods.VIDEO, VisitMethods.CALL]

/**
 * Shows the in-person-visit notice when a flagged patient's visit is opened (QMD-4666).
 *
 * It fires on open rather than when the provider starts the call: a provider who sees it mid-call-
 * start reads it as "the call is already ringing" and clicks through to get rid of it, even though
 * nothing dials until they confirm. Prep time is the moment they can actually act on it.
 *
 * Only `video` and `call` visits qualify. An in-person visit is itself the compliance event, so the
 * patient does not need telling; an async visit has no live conversation to tell them in.
 *
 * Acknowledgement is keyed to the visit and lasts as long as the dashboard is mounted, so a
 * provider is not asked twice for one visit, including after closing and reopening the pop-up. It
 * does not outlive a page load, because there is nowhere durable to put it: backend QMD-4716 only
 * *reads* the flag and nothing clears it but the nightly Snowflake sync. Closing that gap needs an
 * acknowledge endpoint.
 */
export const useInPersonVisitNotice = (
  patientWithVisitInfo: PatientWithVisitInfo | undefined,
): UseInPersonVisitNoticeReturn => {
  const [acknowledgedVisitId, setAcknowledgedVisitId] = useState<string | undefined>(undefined)
  const [isNoticeOpen, setIsNoticeOpen] = useState(false)
  // Mirrors `isNoticeOpen` synchronously: two clicks in one tick see the same render, so a
  // state-based guard is still `true` for the second one. The ref flips immediately.
  const isOpenRef = useRef(false)

  const visitId = patientWithVisitInfo?.VisitId

  // Cache reader, like every other consumer of this query: `useVisitActions` fetches the visit and
  // seeds the cache when the provider opens it, so `enabled: false` keeps this subscribed to that
  // entry without issuing a second request.
  const { data: visitDetails } = useGetVisitsVisitIdV2(
    visitId || '',
    { includeMostRecentMatchingVisit: true },
    { query: { enabled: false } },
  )

  const isNoticedVisitMethod = NOTICED_VISIT_METHODS.includes(patientWithVisitInfo?.VisitMethod ?? '')
  const hasBackendFlag = visitDetails?.ShowInPersonVisitRequiredNotice === true
  const isAcknowledged = !!visitId && acknowledgedVisitId === visitId
  const requiresNotice = isNoticedVisitMethod && hasBackendFlag && !isAcknowledged

  // Held in a ref, not closed over: `useVisitActions` sets the visit first and awaits the patient
  // fetch after, so a callback memoised on `visitId` alone would capture the render where patient
  // id and MRN are still empty and report them that way for the rest of the visit.
  const trackingPropertiesRef = useRef({
    PatientId: patientWithVisitInfo?.PatientId || '',
    PatientMrn: patientWithVisitInfo?.MedicalRecordNumber || '',
    VisitId: visitId || '',
    VisitMethod: patientWithVisitInfo?.VisitMethod || '',
  })
  trackingPropertiesRef.current = {
    PatientId: patientWithVisitInfo?.PatientId || '',
    PatientMrn: patientWithVisitInfo?.MedicalRecordNumber || '',
    VisitId: visitId || '',
    VisitMethod: patientWithVisitInfo?.VisitMethod || '',
  }

  useEffect(() => {
    if (requiresNotice) {
      isOpenRef.current = true
      setIsNoticeOpen(true)
    }
  }, [requiresNotice])

  // Fires once per open rather than on every render of the modal.
  useEffect(() => {
    if (!isNoticeOpen) {
      return
    }

    tracking.trackEvent(PROVIDER_EVENT_NAMES.VIEWED_VISIT_NOTICE, trackingPropertiesRef.current)
  }, [isNoticeOpen])

  const confirmNotice = useCallback(() => {
    // A second click must not fire a second acceptance: `Accepted Visit Notice` is the only record
    // that a provider acknowledged this notice, and a double-fire inflates the one number for it.
    if (!isOpenRef.current) {
      return
    }

    isOpenRef.current = false

    tracking.trackEvent(PROVIDER_EVENT_NAMES.ACCEPTED_VISIT_NOTICE, trackingPropertiesRef.current)

    setAcknowledgedVisitId(visitId)
    setIsNoticeOpen(false)
  }, [visitId])

  return { isNoticeOpen, confirmNotice }
}
